import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Mail, MailCheck, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../services/api';
import { useTranslation } from 'react-i18next';
import AuthLayout from '../../layouts/AuthLayout';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setEmailError(null);

    if (!email.trim()) {
      setEmailError(t('forgot_password.error_email_required'));
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError(t('forgot_password.error_email_invalid'));
      return;
    }

    setLoading(true);
    try {
      await api.post('/forgot-password', { email });
      setSuccess(true);
    } catch {
      setError(t('forgot_password.error_generic'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout>
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border dark:border-slate-700 p-6 sm:p-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
              <MailCheck className="h-8 w-8 text-emerald-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-2">{t('forgot_password.success_title')}</h2>
          <p className="text-sm text-muted-foreground mb-6">{t('forgot_password.success_message')}</p>
          <Link to="/login">
            <Button className="w-full">{t('forgot_password.back_to_login')}</Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border dark:border-slate-700 p-6 sm:p-8">
        <h2 className="text-2xl font-bold text-center mb-1">{t('forgot_password.title')}</h2>
        <p className="text-sm text-muted-foreground text-center mb-6">{t('forgot_password.subtitle')}</p>

        <div className="space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 p-3 rounded-md text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <div className="absolute left-3 top-3 text-muted-foreground">
                <Mail className="h-4 w-4" />
              </div>
              <Input
                type="email"
                placeholder={t('forgot_password.email_placeholder')}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(null); setError(null); }}
                className={`pl-10 ${emailError ? 'border-red-500' : ''}`}
                disabled={loading}
              />
              {emailError && (
                <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {emailError}
                </div>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t('forgot_password.sending') : t('forgot_password.submit_button')}
            </Button>
          </form>

          <div className="text-center">
            <Link to="/login" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
              <ArrowLeft className="h-3 w-3" />
              {t('forgot_password.back_to_login')}
            </Link>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
